import React, { useEffect, useState } from 'react';
import styles from './AdminShared.module.scss';
import { TagCreateDto, TagDto } from '../../../types/admin.types';
import { createAdminTag, deleteAdminTag, fetchAdminTags, updateAdminTag } from '../../../services/admin.service';
import { useUserState } from '../../../providers/user-state.provider.ts';

const emptyForm: TagCreateDto = {
    name: ''
};

const AdminTags = () => {
    const { getUserInfo } = useUserState();
    const [items, setItems] = useState<TagDto[]>([]);
    const [form, setForm] = useState<TagCreateDto>(emptyForm);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [infoMessage, setInfoMessage] = useState<string | null>(null);
    const token = getUserInfo()?.token;

    const loadItems = async () => {
        setIsLoading(true);
        setErrorMessage(null);
        try {
            const response = await fetchAdminTags(token);
            setItems(response);
        } catch (error) {
            setErrorMessage('Unable to load tags.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadItems();
    }, []);

    const resetForm = () => {
        setForm(emptyForm);
        setSelectedId(null);
        setInfoMessage(null);
    };

    const handleEdit = (item: TagDto) => {
        setSelectedId(item.id);
        setForm({ name: item.name });
        setInfoMessage(null);
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setErrorMessage(null);
        setInfoMessage(null);
        try {
            if (selectedId) {
                const result = await updateAdminTag(selectedId, form, token);
                if (result.conflict) {
                    setInfoMessage('Tag name already exists. Using the existing tag instead.');
                }
            } else {
                await createAdminTag(form, token);
            }
            await loadItems();
            resetForm();
        } catch (error) {
            setErrorMessage('Unable to save tag.');
        }
    };

    const handleDelete = async (id: string) => {
        setErrorMessage(null);
        try {
            await deleteAdminTag(id, token);
            if (selectedId === id) {
                resetForm();
            }
            await loadItems();
        } catch (error) {
            setErrorMessage('Unable to delete tag.');
        }
    };

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div>
                    <h1>Tags</h1>
                    <p>Manage tag suggestions and cleanup.</p>
                </div>
            </header>

            {errorMessage ? <div className={styles.muted}>{errorMessage}</div> : null}
            {infoMessage ? <div className={styles.muted}>{infoMessage}</div> : null}

            <div className={styles.content}>
                <section className={styles.panel}>
                    <h2>Existing Tags</h2>
                    {isLoading ? (
                        <div className={styles.muted}>Loading tags...</div>
                    ) : items.length === 0 ? (
                        <div className={styles.empty}>No tags yet.</div>
                    ) : (
                        items.map((item) => (
                            <div key={item.id} className={styles.listItem}>
                                <div>
                                    <strong>{item.name}</strong>
                                    <div className={styles.muted}>Usage: {item.usageCount}</div>
                                </div>
                                <div className={styles.rowActions}>
                                    <button type="button" className={styles.secondary} onClick={() => handleEdit(item)}>Edit</button>
                                    <button type="button" className={styles.danger} onClick={() => handleDelete(item.id)}>Delete</button>
                                </div>
                            </div>
                        ))
                    )}
                </section>

                <section className={styles.panel}>
                    <h2>{selectedId ? 'Edit Tag' : 'New Tag'}</h2>
                    <form onSubmit={handleSubmit}>
                        <div className={styles.field}>
                            <label htmlFor="tag-name">Name</label>
                            <input
                                id="tag-name"
                                value={form.name}
                                onChange={(event) => setForm({ name: event.target.value })}
                                maxLength={200}
                                required
                            />
                        </div>
                        <div className={styles.actions}>
                            <button className={styles.primary} type="submit">
                                {selectedId ? 'Update Tag' : 'Create Tag'}
                            </button>
                            {selectedId ? (
                                <button type="button" className={styles.secondary} onClick={resetForm}>Cancel</button>
                            ) : null}
                        </div>
                    </form>
                </section>
            </div>
        </div>
    );
};

export default AdminTags;
